import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { chatsApi, zohoApi, settingsApi } from "../lib/api";
import {
  LayoutDashboard,
  UserCog,
  Settings,
  LogOut,
  MessagesSquare,
  Mail,
  ClipboardList,
  UserSearch,
  PhoneCall,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import IncomingCallPopup from "./IncomingCallPopup";
import { PhoneStatusPill } from "./BrowserCallWidget";

const SIDEBAR_KEY = "exotic.sidebar.collapsed";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [newMsgCount, setNewMsgCount] = useState(0);
  const [emailOpenCount, setEmailOpenCount] = useState(0);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_KEY);
      // Default to collapsed (icons-only). User explicitly opens to see labels.
      return stored === null ? true : stored === "1";
    } catch { return true; }
  });
  const lastCheckRef = useRef(null);
  const prevCountRef = useRef(0);
  const audioRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleSidebar = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Reflect sidebar width to <main> via a CSS variable so the margin
  // updates in lock-step with the rail width (no flicker / overlap).
  useEffect(() => {
    const width = collapsed ? "64px" : "220px";
    document.documentElement.style.setProperty("--sidebar-width", width);
    document.body.classList.toggle("sidebar-collapsed", collapsed);
    return () => {
      document.documentElement.style.removeProperty("--sidebar-width");
      document.body.classList.remove("sidebar-collapsed");
    };
  }, [collapsed]);

  const playNotificationSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(587.33, now);
      osc.frequency.setValueAtTime(783.99, now + 0.12);
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.25);
    } catch (e) { /* audio not supported */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    const init = async () => {
      try {
        const res = await chatsApi.getNewCount();
        if (res.data.latest_timestamp) lastCheckRef.current = res.data.latest_timestamp;
        prevCountRef.current = res.data.count || 0;
      } catch (e) { /* ignore */ }
    };
    init();
    const interval = setInterval(async () => {
      try {
        const res = await chatsApi.getNewCount(lastCheckRef.current);
        const count = res.data.count || 0;
        if (count > prevCountRef.current && prevCountRef.current > 0) playNotificationSound();
        prevCountRef.current = count;
        setNewMsgCount(count);
        if (res.data.latest_timestamp) lastCheckRef.current = res.data.latest_timestamp;
      } catch (e) { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [user, playNotificationSound]);

  useEffect(() => {
    if (location.pathname === "/chats") setNewMsgCount(0);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const fetchOpen = async () => {
      try {
        const res = await zohoApi.unreadCount();
        if (!cancelled) setEmailOpenCount(res.data.count || 0);
      } catch { }
    };
    fetchOpen();
    const interval = setInterval(fetchOpen, 120000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const [pagePerms, setPagePerms] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await settingsApi.getPagePermissions();
        if (!cancelled) setPagePerms(r.data);
      } catch { /* fall back to hardcoded defaults */ }
    })();
  }, []);

  const role = user?.role;
  const allowedPages = (() => {
    if (role === "super_admin") return null;
    if (pagePerms?.permissions?.[role]) {
      const perms = new Set(pagePerms.permissions[role]);
      if (role === "admin") perms.add("team");
      return perms;
    }
    if (role === "admin") return new Set(["dashboard","chats","customers","emails","performance","reports","calls","settings","team"]);
    if (role === "leader") return new Set(["dashboard","chats","customers","emails","performance","reports","calls","settings","team"]);
    return new Set(["dashboard","chats","customers","emails","reports","calls"]);
  })();
  const canSee = (key) => !allowedPages || allowedPages.has(key);

  // Iter 154 (Feb 27 2026) — trimmed sidebar:
  // Removed Orders, Dup Tickets, Campaigns, AI Orders per user request.
  // Pages still exist at their routes; only the sidebar entries are gone.
  const allNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", key: "dashboard" },
    { to: "/chats", icon: MessagesSquare, label: "Chats", badge: true, key: "chats" },
    { to: "/customers", icon: UserSearch, label: "Customers", key: "customers" },
    { to: "/emails", icon: Mail, label: "Emails", emailBadge: true, key: "emails" },
    { to: "/reports", icon: ClipboardList, label: "Reports", key: "reports" },
    { to: "/calls", icon: PhoneCall, label: "Calls", key: "calls" },
    { to: "/team", icon: UserCog, label: "Team", key: "team" },
    { to: "/settings", icon: Settings, label: "Settings", key: "settings" },
  ];
  const navItems = allNavItems.filter((n) => canSee(n.key));

  return (
    <>
      {/* Left Rail Navigation */}
      <div className={`left-rail ${collapsed ? "is-collapsed" : ""}`} data-testid="left-rail-nav">
        <button
          onClick={toggleSidebar}
          className="rail-toggle"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
        </button>

        <div className="logo" data-testid="rail-brand">
          <img
            src="/logo512.png"
            alt="Exotic India yantra"
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              objectFit: "contain",
              background: "#c96a1a",
              padding: 2,
              display: "block",
              margin: "0 auto",
            }}
          />
        </div>

        <nav className="rail-nav" data-testid="nav-links">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `rail-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "")}`}
              aria-label={item.label}
            >
              <div className="rail-icon">
                <item.icon size={18} />
              </div>
              <span className="rail-label">{item.label}</span>
              <span className="rail-tooltip" role="tooltip">{item.label}</span>
              {item.badge && newMsgCount > 0 && location.pathname !== "/chats" && (
                <div className="badge" data-testid="chat-badge-count">
                  {newMsgCount > 99 ? "99+" : newMsgCount}
                </div>
              )}
              {item.emailBadge && emailOpenCount > 0 && location.pathname !== "/emails" && (
                <div className="badge" style={{ background: "#8b5cf6" }} data-testid="email-badge-count">
                  {emailOpenCount >= 100 ? "100+" : emailOpenCount}
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="rail-user">
          <PhoneStatusPill />
          <div className="rail-avatar" title={user?.name}>
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <button
            onClick={handleLogout}
            className="rail-logout"
            data-testid="logout-btn"
            title="Logout"
            aria-label="Logout"
          >
            <LogOut size={16} />
            <span className="rail-tooltip" role="tooltip">Logout</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <main className="main-content">
        {location.pathname === "/chats" || location.pathname === "/emails" ? (
          <Outlet />
        ) : (
          <div className="content-container p-5 lg:p-6">
            <Outlet />
          </div>
        )}
      </main>

      {/* Floating Telecmi incoming-call popup — visible on every page */}
      <IncomingCallPopup />
    </>
  );
};

export default Layout;
